import React, { useMemo, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import type { RefSplitOrder, SpinReferenceKind } from '../../../Repository/Admin/AdminSpinRepository.js'
import { StatsBarChart } from '../AdminStatsPage/StatsBarChart.js'
import { Card, KpiTile } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import {
  MIN_IDENTITY_OPTIONS,
  REF_ORDER_OPTIONS,
  VERDICT_UI,
  fanoutData,
  normalizeRefIdInput,
  parseMinIdentity,
  refOrderQuestion,
  splitVerdict,
  splitVerdictCounts
} from './spinMetrics.logic.js'
import { NEAR_PERFECT_IDENTITY, REF_KIND_LABEL, isSlowWholeSetRefKind, num, pct, refDetailPath } from './spinRefDetail.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinReference } from './useSpinData.js'

const REF_KINDS: SpinReferenceKind[] = ['NCBI_ID', 'NCBI_TAXID', 'BOLD_BIN']

/**
 * Reference concordance: instead of comparing two SPIN algorithms, compares the current SPIN set
 * against the grouping induced by a reference identifier (NCBI accession, NCBI taxon id or BOLD bin) —
 * does SPIN split the same id across clusters, or keep it together? Reuses the fan-out concordance
 * machinery, and every row of the most-split list drills into [SpinReferenceDetailPage].
 *
 * The ORDERING control is the point of the page, not decoration: raw fan-out ranks the least diagnostic
 * cases first (a promiscuous reference is a property of the reference, not a clustering bug), so the
 * default is the backend's `SUSPICION` — "split despite a near-perfect match". See `refOrderQuestion`.
 *
 * `NCBI_TAXID` is gated behind an explicit load here for the same reason as on the drill-down: its seed
 * column is unindexed, and this endpoint runs three WHOLE-SET statements over it.
 */
export const SpinReferencePage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const qs = params.toString()
  const suffix = qs ? `?${qs}` : ''

  // `picked` is what the selector shows; `activeKind` is what we actually asked the backend for. They
  // only differ while a slow kind waits for its explicit load, so rendered data always matches the label.
  const [picked, setPicked] = useState<SpinReferenceKind>('NCBI_ID')
  const [activeKind, setActiveKind] = useState<SpinReferenceKind>('NCBI_ID')
  const [orderBy, setOrderBy] = useState<RefSplitOrder>('SUSPICION')
  const [minIdentityRaw, setMinIdentityRaw] = useState('')
  const [jumpTo, setJumpTo] = useState('')

  const minIdentity = parseMinIdentity(minIdentityRaw)
  const optIn = picked !== activeKind
  const { reference, loading, error } = useSpinReference(selected, activeKind, orderBy, minIdentity)

  const mostSplit = reference?.mostSplit ?? []
  const verdicts = useMemo(() => splitVerdictCounts(mostSplit), [mostSplit])

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  const label = REF_KIND_LABEL[activeKind]
  const pickKind = (kind: SpinReferenceKind) => {
    setPicked(kind)
    // Fast kinds switch immediately; a slow kind waits for the button below.
    if (!isSlowWholeSetRefKind(kind)) setActiveKind(kind)
  }

  const jump = (e: React.FormEvent) => {
    e.preventDefault()
    const refId = normalizeRefIdInput(picked, jumpTo)
    if (!refId) return
    navigate(refDetailPath(picked, refId, suffix))
  }

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Reference" sel={sel} />

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-semibold">
            Reference concordance
            <InfoTip tip="Looking only at a reference identifier (NCBI accession, NCBI taxon id or BOLD bin), does the SPIN set split the same id across several clusters, or keep it together? Computed over the current member OTUs that carry that identifier." />
          </h2>
          <label className="flex items-center gap-1 text-sm">
            <span className="text-gray-500">Reference</span>
            <select
              className="select select-bordered select-xs"
              value={picked}
              onChange={(e) => pickKind(e.target.value as SpinReferenceKind)}
            >
              {REF_KINDS.map((k) => (
                <option key={k} value={k}>
                  {REF_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-sm">
            <span className="text-gray-500">
              Rank the split list by
              <InfoTip
                tip={`Which question you are asking, not a sort direction. Raw fan-out surfaces promiscuous references (284 accessions match thousands of unrelated sequences at ~92% identity — splitting those is CORRECT); the diagnostic cohort is "split despite a near-perfect match": 933 ids are split across >1 SPIN with a minimum identity ≥ ${NEAR_PERFECT_IDENTITY}%. That is why suspicion is the default, here and in the backend.`}
              />
            </span>
            <select
              className="select select-bordered select-xs"
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value as RefSplitOrder)}
            >
              {REF_ORDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-sm">
            <span className="text-gray-500">
              Minimum identity
              <InfoTip tip="Keeps only reference ids whose WORST carrying OTU still matched at least this well. It also DROPS every id with no readable identity (a BOLD bin without a BOLD blast row), because SQL NULL >= 99.5 is NULL — rows disappear rather than being kept." />
            </span>
            <select
              className="select select-bordered select-xs"
              value={minIdentityRaw}
              onChange={(e) => setMinIdentityRaw(e.target.value)}
            >
              {MIN_IDENTITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn btn-xs btn-primary"
            onClick={() => {
              setOrderBy('SUSPICION')
              setMinIdentityRaw(String(NEAR_PERFECT_IDENTITY))
            }}
            title={`Rank by suspicion and keep only ids whose worst member identity is ≥ ${NEAR_PERFECT_IDENTITY}%`}
          >
            Show the diagnostic cohort
          </button>
        </div>

        <p className="text-sm text-gray-600 max-w-4xl">
          <b>Which question am I asking?</b> {refOrderQuestion(orderBy)}
        </p>

        {/* Always available — the most-split list is capped, and for the slow taxon-id kind this is the
            only way in that does not run the whole-set query at all. */}
        <form className="flex flex-wrap items-center gap-2" onSubmit={jump}>
          <span className="text-sm text-gray-500">
            Jump to any {REF_KIND_LABEL[picked]}
            <InfoTip tip="The most-split list is capped by the backend, so most ids never appear in it. Type or paste any id to open its drill-down directly — accessions are lowercased and a pasted BOLD cluster URL is reduced to its BOLD:… key, exactly as the backend normalises them." />
          </span>
          <input
            className="input input-bordered input-xs font-mono w-72"
            value={jumpTo}
            onChange={(e) => setJumpTo(e.target.value)}
            placeholder={picked === 'BOLD_BIN' ? 'BOLD:ADZ1234' : picked === 'NCBI_TAXID' ? '7227' : 'mn908947.3'}
            aria-label={`Jump to a ${REF_KIND_LABEL[picked]}`}
          />
          <button className="btn btn-xs" type="submit" disabled={normalizeRefIdInput(picked, jumpTo) === ''}>
            Open drill-down
          </button>
        </form>
        {minIdentity != null && (
          <p className="text-sm text-warning">
            Identity floor ≥ {minIdentity}% — reference ids whose identity is <b>unreadable</b> are dropped, not kept: a BOLD bin with no
            BOLD blast row has a NULL minimum identity, and SQL <span className="font-mono">NULL &gt;= {minIdentity}</span> is NULL. The
            KPIs and both fan-out charts below are unfiltered; only the most-split list honours this floor.
          </p>
        )}
      </section>

      {optIn && (
        <section className="space-y-2 border border-warning/40 bg-warning/5 rounded-lg p-4">
          <h3 className="font-semibold">
            Taxon-id concordance loads on demand
            <InfoTip tip="staxids is in no index, so seeding off it scans ~1.9M blocks — and this is the WHOLE-SET query, which runs three such statements. Nothing is fetched until you ask for it." />
          </h3>
          <p className="text-sm text-gray-600">
            The NCBI <b>taxon id</b> view seeds off an unindexed column, and reference concordance is the whole-set query — expect a
            long wait (minutes when cold). The accession and BOLD-bin views load by themselves; this one does not.
          </p>
          <button className="btn btn-sm btn-primary" onClick={() => setActiveKind(picked)} disabled={loading}>
            Load taxon-id concordance
          </button>
        </section>
      )}

      {loading && <p className="text-gray-500">Loading reference concordance…</p>}
      {error && <p className="text-error">Could not load reference concordance: {error}</p>}

      {!optIn && selected && reference && (
        <section className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiTile
              label={
                <>
                  Covered members
                  <InfoTip
                    tip={`Current member OTUs that carry a ${label}. Everything here is computed over exactly these; OTUs without one are excluded.`}
                  />
                </>
              }
              value={reference.coveredMembers.toLocaleString()}
            />
            <KpiTile label={`Distinct ${label}s`} value={reference.refIdCount.toLocaleString()} />
            <KpiTile label="SPINs" value={reference.spinCount.toLocaleString()} />
            <KpiTile
              label={
                <>
                  Split across &gt;1 SPIN
                  <InfoTip
                    tip={`How many ${label}s have their OTUs spread across more than one SPIN. Unfiltered — it ignores the ordering and identity controls. Lower is better, but a split at weak identity is not a bug.`}
                  />
                </>
              }
              value={`${reference.splitRefIdCount.toLocaleString()}${
                reference.refIdCount > 0 ? ` (${((reference.splitRefIdCount / reference.refIdCount) * 100).toFixed(1)}%)` : ''
              }`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title={`Concordance: ${label} → # distinct SPINs`}>
              <StatsBarChart data={fanoutData(reference.refToSpin ?? [])} />
              <p className="text-xs text-gray-500 mt-2">
                For each {label}, how many different SPINs its OTUs fall into. <b>1 = kept together</b> (SPIN keeps the {label} in one
                cluster); &gt;1 = SPIN splits it apart. Bars weighted toward 1 ⇒ SPIN respects the {label}.
              </p>
            </Card>
            <Card title={`Concordance: SPIN → # distinct ${label}s`}>
              <StatsBarChart data={fanoutData(reference.spinToRef ?? [])} />
              <p className="text-xs text-gray-500 mt-2">
                The reverse — for each SPIN, how many different {label}s it lumps together. <b>1 = pure</b> (the SPIN maps to a single{' '}
                {label}); &gt;1 = the SPIN merges several.
              </p>
            </Card>
          </div>

          <Card title={`Most-split ${label}s (${mostSplit.length})`}>
            {mostSplit.length === 0 ? (
              <p className="text-gray-500 text-sm">
                No {label} matches — no id is split across more than one SPIN
                {minIdentity != null ? ` with a readable minimum identity ≥ ${minIdentity}%` : ''}.
              </p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-2">
                  Of the {mostSplit.length.toLocaleString()} listed: <b>{verdicts.SUSPICIOUS}</b> split despite a near-perfect match,{' '}
                  {verdicts.PROMISCUOUS} at weak identity (splitting is correct), {verdicts.AMPLICON} explained by differing amplicons,{' '}
                  {verdicts.UNKNOWN_IDENTITY} with no readable identity. The list is capped by the backend and ordered by the control
                  above.
                </p>
                <div className="overflow-x-auto max-h-[32rem]">
                  <table className="table table-xs table-pin-rows">
                    <thead>
                      <tr>
                        <th>{label}</th>
                        <th className="text-right">
                          Distinct SPINs
                          <InfoTip tip="How many different current SPINs the OTUs carrying this id fall into. >1 = SPIN split it." />
                        </th>
                        <th className="text-right">
                          OTUs
                          <InfoTip tip="Current member OTUs carrying the id — the blast radius of the split, i.e. how much the splitting costs." />
                        </th>
                        <th className="text-right">
                          Min identity
                          <InfoTip tip="The WORST identity any carrying OTU had to this reference. High (≥ 99.5%) means SPIN split despite near-perfect matches; low (~92%) means the reference is promiscuous and splitting is correct. An em dash = no readable blast percentage." />
                        </th>
                        <th className="text-right">
                          Amplicons
                          <InfoTip tip="Distinct amplicons across those SPINs. >1 explains the split outright: a SPIN is per-amplicon, so the same id on CO1 and 16S MUST land in different SPINs." />
                        </th>
                        <th>
                          Reading
                          <InfoTip tip="Verdict from the three columns to the left alone: amplicon spread first (it fully explains a split), then the identity signal." />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {mostSplit.map((r) => {
                        const verdict = splitVerdict(r)
                        const ui = VERDICT_UI[verdict]
                        return (
                          <tr key={r.refId} className={verdict === 'SUSPICIOUS' ? 'bg-warning/10' : undefined}>
                            <td className="font-mono break-all">
                              <Link to={refDetailPath(activeKind, r.refId, suffix)} className="link link-primary">
                                {r.refId}
                              </Link>
                            </td>
                            <td className="text-right tabular-nums">{num(r.spinCount)}</td>
                            <td className="text-right tabular-nums">{num(r.otuCount)}</td>
                            <td className="text-right tabular-nums">{pct(r.minHitPercentage)}</td>
                            <td className="text-right tabular-nums">{num(r.ampliconCount)}</td>
                            <td>
                              <span className={`badge badge-xs ${ui.badge}`} title={ui.tip}>
                                {ui.label}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>
        </section>
      )}
    </div>
  )
}
